
/**
 * Date Utility for enforcing Asia/Taipei (UTC+8) timezone display.
 */

const TIMEZONE = 'Asia/Taipei';

/**
 * Formats a date string or object into 'YYYY-MM-DD HH:mm' in Taipei time.
 * @param {string|Date} date 
 * @returns {string}
 */
export const formatDateTime = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleString('zh-TW', {
        timeZone: TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).replace(/\//g, '-');
};

/**
 * Formats a date string or object into 'YYYY-MM-DD' in Taipei time.
 * @param {string|Date} date 
 * @returns {string}
 */
export const formatDate = (date) => {
    if (!date) return '-';
    try {
        const d = new Date(date);
        return d.toLocaleDateString('zh-TW', {
            timeZone: TIMEZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).replace(/\//g, '-');
    } catch (e) {
        return '-';
    }
};

/**
 * Formats a date string or object into 'YYYY/MM/DD HH:mm:ss' in Taipei time.
 * For audit-log style displays (scan events, card issue time) that need second precision.
 * @param {string|Date} date
 * @returns {string}
 */
export const formatDateTimeSeconds = (date) => {
    if (!date) return '--';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '--';
    return d.toLocaleString('zh-TW', {
        timeZone: TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
};

/**
 * Formats a date string or object into 'HH:mm:ss' in Taipei time.
 * @param {string|Date} date
 * @returns {string}
 */
export const formatTimeSeconds = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('zh-TW', {
        timeZone: TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
};

/**
 * Returns the current date in 'YYYY-MM-DD' format based on Taipei Time.
 * Used for setting default form values.
 * @returns {string}
 */
export const getTodayDateString = () => {
    const d = new Date();
    return d.toLocaleDateString('zh-TW', {
        timeZone: TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).replace(/\//g, '-');
};

/**
 * Formats a date string or object into 'HH:mm' in Taipei time.
 * @param {string|Date} date 
 * @returns {string}
 */
export const formatTime = (date) => {
    if (!date) return '-';
    // Create Date object from input
    const d = new Date(date);

    // Check if d is valid
    if (isNaN(d.getTime())) return '-';

    // Use Intl.DateTimeFormat with explicit timezone
    // This is the standard modern way.
    // If this failed previously, we can try to force it or maybe the input 'date' is ambiguous.
    // Let's add the 'hour12: false' and ensure consistent formatting.
    // Force +8 Calculation (Manual) to avoid browser shifting
    // Reason: new Date(str) already parses local or UTC based on format.
    // If str is "2025-01-01T12:00:00Z", then:
    // We want to show "20:00".

    try {
        // Method 1: Use Intl with timeZone (Robust)
        return new Intl.DateTimeFormat('zh-TW', {
            timeZone: 'Asia/Taipei',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        }).format(d);
    } catch (e) {
        // Fallback: Add 8 hours to UTC time
        const utcMs = d.getTime() + (d.getTimezoneOffset() * 60000); // UTC ms
        const twMs = utcMs + (8 * 60 * 60 * 1000); // UTC+8 ms
        const twDate = new Date(twMs);
        return `${twDate.getHours().toString().padStart(2, '0')}:${twDate.getMinutes().toString().padStart(2, '0')}`;
    }
};

/**
 * Returns the current date in Taipei Time as a Date object (simulated mostly for formatting)
 * or simply returns new Date(). 
 * Note: JS Date objects are always stored in UTC or system local. 
 * Use this only if you need to manipulate components.
 */
export const getNow = () => {
    return new Date();
};
